/** Subscriptions hooks with mock fallback. */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { SUBSCRIPTIONS_QUERY_PREFIX, queryKeys } from "@/lib/query-client";
import type { Subscription } from "@/types";
import { useAuthStore } from "@/features/auth/auth.store";
import { subscriptionsApi } from "./subscriptions.api";
import { toSubscription, toSubscriptionWriteBody, toMockSubscription, type SubscriptionInput } from "./subscriptions.adapter";
import { useSubscriptionsStore } from "./subscriptions.store";

const LIST_PARAMS = { limit: 100, sortBy: "createdAt", sortOrder: "desc" as const };

export type DataSource = "api" | "mock";

export interface UseSubscriptionsResult {
  subscriptions: Subscription[];
  isLoading: boolean;
  isError: boolean;
  isFetching: boolean;
  source: DataSource;
  error: unknown;
  refetch: () => void;
}

export function useSubscriptions(): UseSubscriptionsResult {
  const authed = useAuthStore((s) => s.status === "authenticated");
  const mockActive = useSubscriptionsStore((s) => s.active);

  const query = useQuery({
    queryKey: queryKeys.subscriptionsList(LIST_PARAMS),
    queryFn: () => subscriptionsApi.list(LIST_PARAMS),
    enabled: authed,
  });

  if (!authed) return { subscriptions: mockActive, isLoading: false, isError: false, isFetching: false, source: "mock", error: null, refetch: query.refetch };
  if (query.data) return { subscriptions: query.data.data.map(toSubscription), isLoading: false, isError: false, isFetching: query.isFetching, source: "api", error: null, refetch: query.refetch };
  if (query.isError) return { subscriptions: mockActive, isLoading: false, isError: true, isFetching: false, source: "mock", error: query.error, refetch: query.refetch };
  return { subscriptions: [], isLoading: query.isLoading, isError: false, isFetching: query.isFetching, source: "api", error: null, refetch: query.refetch };
}

function useInvalidateSubscriptions() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: SUBSCRIPTIONS_QUERY_PREFIX });
}

export function useCreateSubscription() {
  const authed = useAuthStore((s) => s.status === "authenticated");
  const add = useSubscriptionsStore((s) => s.add);
  const invalidate = useInvalidateSubscriptions();
  return useMutation({
    mutationFn: async (input: SubscriptionInput) => {
      if (authed) return subscriptionsApi.create(toSubscriptionWriteBody(input));
      const mock = toMockSubscription(input);
      add(mock);
      return mock;
    },
    onSuccess: () => {
      if (authed) void invalidate();
    },
  });
}

export function useUpdateSubscription() {
  const authed = useAuthStore((s) => s.status === "authenticated");
  const update = useSubscriptionsStore((s) => s.update);
  const invalidate = useInvalidateSubscriptions();
  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: SubscriptionInput }) => {
      if (authed) return subscriptionsApi.update(id, toSubscriptionWriteBody(input));
      update(id, { ...input });
      return null;
    },
    onSuccess: () => {
      if (authed) void invalidate();
    },
  });
}

export function useArchiveSubscription() {
  const authed = useAuthStore((s) => s.status === "authenticated");
  const archive = useSubscriptionsStore((s) => s.archive);
  const invalidate = useInvalidateSubscriptions();
  return useMutation({
    mutationFn: async (id: string) => {
      if (authed) return subscriptionsApi.archive(id);
      archive(id);
      return null;
    },
    onSuccess: () => {
      if (authed) void invalidate();
    },
  });
}

export function useRestoreSubscription() {
  const authed = useAuthStore((s) => s.status === "authenticated");
  const restore = useSubscriptionsStore((s) => s.restore);
  const invalidate = useInvalidateSubscriptions();
  return useMutation({
    mutationFn: async (id: string) => {
      if (authed) return subscriptionsApi.restore(id);
      restore(id);
      return null;
    },
    onSuccess: () => {
      if (authed) void invalidate();
    },
  });
}
